import { TermDto, UserDto } from '@act/shared/data-transfer-objects';
import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import { useCallback } from 'react';
import { TaggableChip, ChipColor } from '@act/storybook';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import ConsentHistoryEntry from './ConsentHistoryEntry';
import CloseableDialogTitle from './components/dialog-components/CloseableDialogTitle';

export interface ViewConsentsHistoryDialogUniqueProps {
  member: UserDto;
  terms?: TermDto[];
}

export type ViewConsentsHistoryCallbackInput = null;
export type ViewConsentsHistoryDialogProps = GenericDialogProps<null> &
  ViewConsentsHistoryDialogUniqueProps;

export const ViewConsentsHistoryDialog = ({
  member,
  terms,
  open,
  setOpen,
}: ViewConsentsHistoryDialogProps) => {
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  function getTermChip(term: TermDto): JSX.Element {
    let label = 'Incomplete';
    let type: ChipColor = 'warning';

    if (term.consented === true) {
      label = 'Accepted';
      type = 'success';
    } else if (term.consented === false) {
      label = 'Rejected';
      type = 'error';
    }
    return <TaggableChip label={label} color={type} isTag />;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm">
      <CloseableDialogTitle
        title={`${member?.firstName}'s consents`}
        handleClose={handleClose}
      />
      <DialogContent sx={{ padding: '12px 24px 24px' }}>
        {!terms?.length && <Typography>No record found</Typography>}
        {terms?.length && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            {terms.map(term => (
              <Box key={term.id}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    alignItems: 'center',

                    '&:first-of-type': {
                      marginBottom: '12px',
                    },
                  }}
                >
                  <Typography variant="titleMedium">{term.name}</Typography>
                  {getTermChip(term)}
                </Box>
                {term.consented === null && (
                  <Typography variant="bodyMedium">{`${term.name} has not yet been accepted or rejected.`}</Typography>
                )}
                {term.consented !== null && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <ConsentHistoryEntry
                      consented={term.consented}
                      termResponseSubmission={term.termResponseSubmission}
                    />
                    {term?.history?.map((entry: Partial<TermDto>) => {
                      return (
                        <ConsentHistoryEntry
                          key={entry.id}
                          consented={entry.consented}
                          termResponseSubmission={entry.termResponseSubmission}
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewConsentsHistoryDialog;
